import {
  ANCHORS,
  anchorToPoint,
  connectorBound,
  connectorPoints as buildConnectorPoints,
  distanceToSegment,
  getCommonBound,
  orthPath,
  resolveEndpoint,
} from './geometry';
import { LayerManager } from './LayerManager';
import { SelectionManager } from './SelectionManager';
import { SurfaceModel } from './SurfaceModel';
import { viewportForStop, type TourStop } from '../tour';
import { Viewport, FIT_TO_SCREEN_PADDING } from './Viewport';
import {
  DEFAULT_SIZE,
  BRUSH_COLORS,
  MARKER_COLORS,
  PIN_COLORS,
  REDACT_COLORS,
  ROPE_COLORS,
  STAMP_COLORS,
  STAMP_PRESETS,
  TAPE_COLORS,
  type BrushProps,
  type CircleProps,
  type ConnectorEndpoint,
  type ConnectorProps,
  type ElementType,
  type FrameProps,
  type GfxElement,
  type HighlightProps,
  type IBound,
  type IVec,
  type NoteColor,
  type NoteProps,
  type PhotoProps,
  type PinProps,
  type RedactProps,
  type RefKind,
  type RopeProps,
  type ShapeKind,
  type ShapeProps,
  type StampProps,
  type TapeProps,
  type TextProps,
} from './types';

export type ToolName =
  | 'select'
  | 'hand'
  | 'note'
  | 'shape'
  | 'text'
  | 'frame'
  | 'connector'
  | 'rope'
  | 'pin'
  | 'circle'
  | 'tape'
  | 'highlight'
  | 'brush'
  | 'stamp'
  | 'redact'
  | 'eraser';

export type BoardStyle = 'corkboard' | 'paper' | 'grid';

type Interaction =
  | { mode: 'idle' }
  | { mode: 'panning'; last: IVec }
  | { mode: 'drag'; start: IVec; last: IVec; moved: boolean }
  | {
      mode: 'resize';
      handle: IVec;
      startBound: IBound;
      startElements: { id: string; x: number; y: number; w: number; h: number }[];
    }
  | { mode: 'marquee'; start: IVec; additive: boolean }
  | { mode: 'connect'; source: ConnectorEndpoint; start: IVec; targetId?: string; link: 'connector' | 'rope' }
  | { mode: 'draw'; kind: 'circle' | 'highlight'; start: IVec }
  | { mode: 'ink'; points: IVec[] }
  | { mode: 'endpoint'; linkId: string; end: 'source' | 'target' };

let counter = 0;
export function randomId(prefix = 'el'): string {
  counter += 1;
  return `${prefix}_${Date.now().toString(36)}_${counter.toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 7)}`;
}

/** 缩放/拖拽的手柄锚点。 */
const HANDLES: IVec[] = [
  [0, 0],
  [0.5, 0],
  [1, 0],
  [1, 0.5],
  [1, 1],
  [0.5, 1],
  [0, 1],
  [0, 0.5],
];

const HANDLE_CURSORS = [
  'nwse-resize',
  'ns-resize',
  'nesw-resize',
  'ew-resize',
  'nwse-resize',
  'ns-resize',
  'nesw-resize',
  'ew-resize',
];

export type GfxEvents = {
  change: 'change';
};

/** 自动巡航运行时状态（供 UI 读取）。 */
export type TourRuntime = {
  active: boolean;
  index: number;
  total: number;
  title: string;
  speed: number;
  paused: boolean;
};

type TourInternal = {
  stops: TourStop[];
  index: number;
  phase: 'move' | 'hold';
  phaseStart: number;
  from: { viewportX: number; viewportY: number; zoom: number };
  to: { viewportX: number; viewportY: number; zoom: number };
  speed: number;
  paused: boolean;
  raf: number | null;
  lastNotify: number;
};

/**
 * 图形控制器（借鉴 AFFiNE GfxController）：渲染与模型之间的唯一门面，
 * 聚合视口、数据模型、图层、选择与工具，并驱动 rAF 渲染循环。
 */
export class GfxController {
  readonly viewport = new Viewport();
  readonly surface = new SurfaceModel();
  readonly layer: LayerManager;
  readonly selection: SelectionManager;

  tool: ToolName = 'select';
  hoverId: string | null = null;
  activeMarquee: IBound | null = null;
  connectorPreview: IVec[] | null = null;
  drawPreview: IBound | null = null;
  /** 手绘预览点（模型坐标）。 */
  inkPreview: IVec[] | null = null;
  /** 端点拖拽时吸附到的元素 id。 */
  endpointSnapId: string | null = null;
  brushColor = BRUSH_COLORS[0];
  brushMode: 'pencil' | 'marker' = 'pencil';
  stampText = STAMP_PRESETS[0];
  stampColor = STAMP_COLORS[0];
  boardStyle: BoardStyle = 'corkboard';
  spacePressed = false;
  /** 只读模式（全屏预览）：仅允许平移/缩放/选择，不允许编辑。 */
  readonly = false;

  /** 渲染回调，由 React 层注入。 */
  renderer: { render: () => void; resize: (w: number, h: number) => void; syncDom: () => void } | null =
    null;

  private container: HTMLElement;
  private interaction: Interaction = { mode: 'idle' };
  private raf: number | null = null;
  private domDirty = true;
  private listeners = new Set<() => void>();
  private history: string[] = [];
  private future: string[] = [];
  private detach: (() => void)[] = [];
  private tour: TourInternal | null = null;

  constructor(container: HTMLElement) {
    this.container = container;
    this.layer = new LayerManager(this.surface);
    this.selection = new SelectionManager(this.surface);
    this.surface.on(() => {
      this.domDirty = true;
      this.scheduleRender();
    });
    this.selection.onChange(() => this.notifyChange());
    this.viewport.onChange(() => {
      this.domDirty = true;
      this.scheduleRender();
    });
    this.attachEvents();
  }

  // ── 生命周期 ──────────────────────────────────────────
  destroy(): void {
    this.stopTour();
    for (const fn of this.detach) fn();
    this.detach = [];
    if (this.raf) cancelAnimationFrame(this.raf);
  }

  /** 每帧渲染，合并高频更新。 */
  private scheduleRender(): void {
    if (this.raf !== null) return;
    this.raf = requestAnimationFrame(() => {
      this.raf = null;
      this.renderer?.render();
      if (this.domDirty) {
        this.domDirty = false;
        this.renderer?.syncDom();
      }
    });
  }

  /** 立即渲染。 */
  requestRender(): void {
    this.renderer?.render();
    if (this.domDirty) {
      this.domDirty = false;
      this.renderer?.syncDom();
    }
  }

  notifyChange(): void {
    for (const fn of this.listeners) fn();
  }

  onChange(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  // ── 历史记录 ──────────────────────────────────────────
  private snapshot(): string {
    return JSON.stringify(this.surface.all());
  }

  pushHistory(): void {
    this.history.push(this.snapshot());
    if (this.history.length > 60) this.history.shift();
    this.future = [];
  }

  undo(): void {
    const prev = this.history.pop();
    if (!prev) return;
    this.future.push(this.snapshot());
    this.restore(prev);
  }

  redo(): void {
    const next = this.future.pop();
    if (!next) return;
    this.history.push(this.snapshot());
    this.restore(next);
  }

  private restore(json: string): void {
    this.surface.replaceAll(JSON.parse(json) as GfxElement[]);
    this.selection.prune();
    this.domDirty = true;
    this.scheduleRender();
    this.notifyChange();
  }

  // ── 自动巡航 ────────────────────────────────────────
  get tourState(): TourRuntime {
    const t = this.tour;
    return {
      active: Boolean(t),
      index: t?.index ?? 0,
      total: t?.stops.length ?? 0,
      title: t?.stops[t.index]?.title ?? '',
      speed: t?.speed ?? 1,
      paused: t?.paused ?? false,
    };
  }

  startTour(stops: TourStop[], speed = 1): void {
    if (stops.length === 0) {
      this.notifyChange();
      return;
    }
    this.stopTour();
    this.tour = {
      stops,
      index: 0,
      phase: 'move',
      phaseStart: performance.now(),
      from: { viewportX: this.viewport.viewportX, viewportY: this.viewport.viewportY, zoom: this.viewport.zoom },
      to: this.targetFor(0),
      speed,
      paused: false,
      raf: null,
      lastNotify: 0,
    };
    this.tour.raf = requestAnimationFrame(this.tourTick);
    this.notifyChange();
  }

  stopTour(): void {
    if (this.tour?.raf != null) cancelAnimationFrame(this.tour.raf);
    this.tour = null;
    this.notifyChange();
  }

  toggleTourPause(): void {
    if (!this.tour) return;
    this.tour.paused = !this.tour.paused;
    this.tour.phaseStart = performance.now();
    this.notifyChange();
  }

  setTourSpeed(speed: number): void {
    if (!this.tour) return;
    this.tour.speed = Math.max(0.25, Math.min(3, speed));
    this.notifyChange();
  }

  tourNext(): void {
    if (!this.tour) return;
    this.advanceTour(1);
  }

  tourPrev(): void {
    if (!this.tour) return;
    const t = this.tour;
    t.index = (t.index - 1 + t.stops.length) % t.stops.length;
    t.phase = 'move';
    t.phaseStart = performance.now();
    t.from = { viewportX: this.viewport.viewportX, viewportY: this.viewport.viewportY, zoom: this.viewport.zoom };
    t.to = this.targetFor(t.index);
    this.notifyChange();
  }

  private targetFor(index: number): { viewportX: number; viewportY: number; zoom: number } {
    const stop = this.tour?.stops[index];
    if (!stop) {
      return { viewportX: this.viewport.viewportX, viewportY: this.viewport.viewportY, zoom: this.viewport.zoom };
    }
    return viewportForStop(stop, {
      width: this.viewport.width || 1200,
      height: this.viewport.height || 800,
      minZoom: this.viewport.minZoom,
      maxZoom: this.viewport.maxZoom,
    });
  }

  private advanceTour(step: number): void {
    const t = this.tour;
    if (!t) return;
    t.index = (t.index + step + t.stops.length) % t.stops.length;
    t.phase = 'move';
    t.phaseStart = performance.now();
    t.from = { viewportX: this.viewport.viewportX, viewportY: this.viewport.viewportY, zoom: this.viewport.zoom };
    t.to = this.targetFor(t.index);
    this.notifyChange();
  }

  private tourTick = (now: number): void => {
    const t = this.tour;
    if (!t) return;

    if (!t.paused) {
      const moveDuration = 1500 / t.speed;
      const holdDuration = 2400 / t.speed;

      if (t.phase === 'move') {
        const progress = Math.min(1, (now - t.phaseStart) / moveDuration);
        const eased = easeInOutCubic(progress);
        this.viewport.zoom = lerp(t.from.zoom, t.to.zoom, eased);
        this.viewport.viewportX = lerp(t.from.viewportX, t.to.viewportX, eased);
        this.viewport.viewportY = lerp(t.from.viewportY, t.to.viewportY, eased);
        this.viewport.notify();
        if (progress >= 1) {
          t.phase = 'hold';
          t.phaseStart = now;
        }
      } else if (now - t.phaseStart >= holdDuration) {
        this.advanceTour(1);
      }

      if (now - t.lastNotify > 220) {
        t.lastNotify = now;
        this.notifyChange();
      }
    }

    t.raf = requestAnimationFrame(this.tourTick);
  };

  // ── 视口 ──────────────────────────────────────────────
  resize(width: number, height: number): void {
    const rect = this.container.getBoundingClientRect();
    this.viewport.setContainer(rect.left, rect.top, width, height);
    this.renderer?.resize(width, height);
    this.domDirty = true;
    this.scheduleRender();
  }

  zoomIn(anchor?: IVec): void {
    this.viewport.zoomIn(anchor);
    this.notifyChange();
  }

  zoomOut(anchor?: IVec): void {
    this.viewport.zoomOut(anchor);
    this.notifyChange();
  }

  resetZoom(): void {
    this.viewport.zoomTo(1);
    this.notifyChange();
  }

  fitToScreen(): void {
    const bound = this.surface.elementsBound;
    const selected = this.selection.bound;
    this.viewport.fitToScreen(selected ?? bound ?? { x: 0, y: 0, w: 0, h: 0 }, FIT_TO_SCREEN_PADDING);
    this.notifyChange();
  }

  setViewport(state: { viewportX: number; viewportY: number; zoom: number }): void {
    this.viewport.viewportX = state.viewportX;
    this.viewport.viewportY = state.viewportY;
    this.viewport.zoomTo(state.zoom);
    this.notifyChange();
  }

  // ── 连线几何 ──────────────────────────────────────────
  connectorPoints(source: ConnectorEndpoint, target: ConnectorEndpoint, mode: 'straight' | 'orth' = 'straight'): IVec[] {
    return buildConnectorPoints(source, target, mode, this.surface.lookup);
  }

  // ── 元素创建 ──────────────────────────────────────────
  private placeAt(center: IVec, size: { w: number; h: number }): { x: number; y: number } {
    return { x: center[0] - size.w / 2, y: center[1] - size.h / 2 };
  }

  addElement(element: GfxElement): GfxElement {
    this.pushHistory();
    this.surface.add(element);
    this.notifyChange();
    return element;
  }

  addNote(center: IVec, overrides: Partial<NoteProps> = {}): GfxElement {
    const size = DEFAULT_SIZE.note;
    const pos = this.placeAt(center, size);
    return this.addElement({
      id: randomId('note'),
      type: 'note',
      ...pos,
      ...size,
      rotate: 0,
      z: this.layer.nextTopZ(),
      group: null,
      locked: false,
      hidden: false,
      local: false,
      props: { text: '', color: 'blue', title: '情报卡片', ...overrides } as NoteProps,
      refKind: overrides.refKind,
      refId: overrides.refId,
    });
  }

  addShape(center: IVec, shape: ShapeKind = 'rounded'): GfxElement {
    const size = DEFAULT_SIZE.shape;
    const pos = this.placeAt(center, size);
    return this.addElement({
      id: randomId('shape'),
      type: 'shape',
      ...pos,
      ...size,
      rotate: 0,
      z: this.layer.nextTopZ(),
      group: null,
      locked: false,
      hidden: false,
      local: false,
      props: {
        shape,
        fill: '#ffffff',
        stroke: '#475569',
        text: '',
        textColor: '#1f2937',
      } as ShapeProps,
    });
  }

  addText(center: IVec, text = '双击编辑文本'): GfxElement {
    const size = DEFAULT_SIZE.text;
    const pos = this.placeAt(center, size);
    return this.addElement({
      id: randomId('text'),
      type: 'text',
      ...pos,
      ...size,
      rotate: 0,
      z: this.layer.nextTopZ(),
      group: null,
      locked: false,
      hidden: false,
      local: false,
      props: { text, color: '#1f2937', fontSize: 20, bold: false } as TextProps,
    });
  }

  addFrame(center: IVec): GfxElement {
    const size = DEFAULT_SIZE.frame;
    const pos = this.placeAt(center, size);
    return this.addElement({
      id: randomId('frame'),
      type: 'frame',
      ...pos,
      ...size,
      rotate: 0,
      z: this.layer.nextBottomZ(),
      group: null,
      locked: false,
      hidden: false,
      local: false,
      props: { title: '分组框', color: '#78716c' } as FrameProps,
    });
  }

  addConnector(source: ConnectorEndpoint, target: ConnectorEndpoint, mode: 'straight' | 'orth' = 'orth', label?: string): GfxElement {
    const props: ConnectorProps = { source, target, mode, stroke: '#78716c', label };
    const points = this.connectorPoints(source, target, mode);
    const bound = connectorBound(points, 0);
    return this.addElement({
      id: randomId('conn'),
      type: 'connector',
      x: bound.x,
      y: bound.y,
      w: bound.w,
      h: bound.h,
      rotate: 0,
      z: this.layer.nextTopZ(),
      group: null,
      locked: false,
      hidden: false,
      local: false,
      props: props as unknown as Record<string, unknown>,
    });
  }

  // ── 侦探组件 ────────────────────────────────────────────
  addRope(
    source: ConnectorEndpoint,
    target: ConnectorEndpoint,
    label?: string,
    color = ROPE_COLORS[0]
  ): GfxElement {
    const props: RopeProps = { source, target, color, sag: 0.14, width: 2.5, label };
    return this.addElement({
      id: randomId('rope'),
      type: 'rope',
      x: 0,
      y: 0,
      w: 0,
      h: 0,
      rotate: 0,
      z: this.layer.nextTopZ(),
      group: null,
      locked: false,
      hidden: false,
      local: false,
      props: props as unknown as Record<string, unknown>,
    });
  }

  addPin(center: IVec, color = PIN_COLORS[0], kind: PinProps['kind'] = 'push', attachTo?: string): GfxElement {
    const size = DEFAULT_SIZE.pin;
    const pos = this.placeAt(center, size);
    return this.addElement({
      id: randomId('pin'),
      type: 'pin',
      ...pos,
      ...size,
      rotate: 0,
      z: this.layer.nextTopZ(),
      group: null,
      locked: false,
      hidden: false,
      local: false,
      attachTo,
      props: { color, kind } as PinProps as unknown as Record<string, unknown>,
    });
  }

  addCircle(bound: IBound, color = ROPE_COLORS[0]): GfxElement {
    const b =
      bound.w < 24 || bound.h < 24
        ? { x: bound.x - 140, y: bound.y - 90, w: 280, h: 180 }
        : bound;
    return this.addElement({
      id: randomId('circle'),
      type: 'circle',
      ...b,
      rotate: 0,
      z: this.layer.nextTopZ(),
      group: null,
      locked: false,
      hidden: false,
      local: false,
      props: {
        color,
        strokeWidth: 2.5,
        dashed: false,
        seed: Math.floor(Math.random() * 100000) + 1,
      } as CircleProps as unknown as Record<string, unknown>,
    });
  }

  addTape(center: IVec, color = TAPE_COLORS[0], attachTo?: string): GfxElement {
    const size = DEFAULT_SIZE.tape;
    const pos = this.placeAt(center, size);
    return this.addElement({
      id: randomId('tape'),
      type: 'tape',
      ...pos,
      ...size,
      rotate: -6 + Math.random() * 12,
      z: this.layer.nextTopZ(),
      group: null,
      locked: false,
      hidden: false,
      local: false,
      attachTo,
      props: {
        color,
        seed: Math.floor(Math.random() * 100000) + 1,
      } as TapeProps as unknown as Record<string, unknown>,
    });
  }

  addHighlight(bound: IBound, color = MARKER_COLORS[0]): GfxElement {
    const b =
      bound.w < 16 ? { x: bound.x, y: bound.y, w: 220, h: 40 } : bound;
    return this.addElement({
      id: randomId('hl'),
      type: 'highlight',
      ...b,
      rotate: 0,
      z: this.layer.nextTopZ(),
      group: null,
      locked: false,
      hidden: false,
      local: false,
      props: { color } as HighlightProps as unknown as Record<string, unknown>,
    });
  }

  addRedact(center: IVec): GfxElement {
    const size = DEFAULT_SIZE.redact;
    const pos = this.placeAt(center, size);
    return this.addElement({
      id: randomId('redact'),
      type: 'redact',
      ...pos,
      ...size,
      rotate: 0,
      z: this.layer.nextTopZ(),
      group: null,
      locked: false,
      hidden: false,
      local: false,
      props: { color: REDACT_COLORS[0] } as RedactProps as unknown as Record<string, unknown>,
    });
  }

  addPhoto(center: IVec, src: string, caption = ''): GfxElement {
    const size = DEFAULT_SIZE.photo;
    const pos = this.placeAt(center, size);
    return this.addElement({
      id: randomId('photo'),
      type: 'photo',
      ...pos,
      ...size,
      rotate: -2 + Math.random() * 4,
      z: this.layer.nextTopZ(),
      group: null,
      locked: false,
      hidden: false,
      local: false,
      props: { src, caption, frame: 'polaroid' } as PhotoProps as unknown as Record<string, unknown>,
    });
  }

  /** 自由手绘：传入模型坐标点列。 */
  addBrush(points: IVec[], opts: { color?: string; width?: number; mode?: 'pencil' | 'marker' } = {}): GfxElement | null {
    if (points.length < 2) return null;
    const xs = points.map(p => p[0]);
    const ys = points.map(p => p[1]);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    const maxX = Math.max(...xs);
    const maxY = Math.max(...ys);
    const relative = points.map(p => [p[0] - minX, p[1] - minY] as IVec);
    return this.addElement({
      id: randomId('brush'),
      type: 'brush',
      x: minX,
      y: minY,
      w: Math.max(4, maxX - minX),
      h: Math.max(4, maxY - minY),
      rotate: 0,
      z: this.layer.nextTopZ(),
      group: null,
      locked: false,
      hidden: false,
      local: false,
      props: {
        points: relative,
        color: opts.color ?? this.brushColor,
        width: opts.width ?? (this.brushMode === 'marker' ? 6 : 3),
        mode: opts.mode ?? this.brushMode,
        seed: Math.floor(Math.random() * 100000) + 1,
      } as BrushProps as unknown as Record<string, unknown>,
    });
  }

  /** 印章。 */
  addStamp(center: IVec, opts: { text?: string; color?: string; shape?: 'round' | 'rect' } = {}): GfxElement {
    const size = DEFAULT_SIZE.stamp;
    const pos = this.placeAt(center, size);
    return this.addElement({
      id: randomId('stamp'),
      type: 'stamp',
      ...pos,
      ...size,
      rotate: -8,
      z: this.layer.nextTopZ(),
      group: null,
      locked: false,
      hidden: false,
      local: false,
      props: {
        text: opts.text ?? this.stampText,
        color: opts.color ?? this.stampColor,
        shape: opts.shape ?? 'round',
        seed: Math.floor(Math.random() * 100000) + 1,
      } as StampProps as unknown as Record<string, unknown>,
    });
  }

  setBoardStyle(style: BoardStyle): void {
    this.boardStyle = style;
    this.domDirty = true;
    this.scheduleRender();
    this.notifyChange();
  }

  setReadonly(value: boolean): void {
    this.readonly = value;
    if (value) this.selection.clear();
    this.notifyChange();
  }

  /** 从业务实体创建便签节点。 */
  addEntityNode(
    center: IVec,
    data: { title: string; text: string; color?: NoteColor; refKind?: RefKind; refId?: string }
  ): GfxElement {
    return this.addNote(center, {
      title: data.title,
      text: data.text,
      color: data.color ?? 'blue',
      refKind: data.refKind,
      refId: data.refId,
    });
  }

  // ── 选择与编辑操作 ────────────────────────────────────
  deleteSelection(): void {
    if (this.selection.size === 0) return;
    this.pushHistory();
    for (const id of this.selection.ids) this.surface.remove(id);
    this.selection.clear();
    this.notifyChange();
  }

  duplicateSelection(): void {
    const elements = this.selection.elements;
    if (elements.length === 0) return;
    this.pushHistory();
    const newIds: string[] = [];
    for (const el of elements) {
      if (el.type === 'connector' || el.type === 'rope' || el.type === 'pin') continue;
      const id = randomId(el.type);
      this.surface.add({
        ...el,
        id,
        x: el.x + 32,
        y: el.y + 32,
        z: this.layer.nextTopZ(),
      });
      newIds.push(id);
    }
    this.selection.set(newIds);
    this.notifyChange();
  }

  groupSelection(): void {
    if (this.selection.size < 2) return;
    this.pushHistory();
    const elements = this.selection.elements.filter(el => el.type !== 'connector');
    const bound = getCommonBound(elements.map(el => ({ x: el.x, y: el.y, w: el.w, h: el.h })));
    if (!bound) return;
    const groupId = randomId('group');
    this.surface.add({
      id: groupId,
      type: 'group',
      x: bound.x,
      y: bound.y,
      w: bound.w,
      h: bound.h,
      rotate: 0,
      z: this.layer.nextBottomZ(),
      group: null,
      locked: false,
      hidden: false,
      local: false,
      props: { children: elements.map(el => el.id) } as unknown as Record<string, unknown>,
    });
    for (const el of elements) this.surface.setGroup(el.id, groupId);
    this.selection.set([groupId]);
    this.notifyChange();
  }

  ungroupSelection(): void {
    const groups = this.selection.elements.filter(el => el.type === 'group' || el.type === 'frame');
    if (groups.length === 0) return;
    this.pushHistory();
    const freed: string[] = [];
    for (const group of groups) {
      for (const child of this.surface.childrenOf(group.id)) {
        this.surface.setGroup(child.id, null);
        freed.push(child.id);
      }
      this.surface.remove(group.id);
    }
    this.selection.set(freed);
    this.notifyChange();
  }

  bringToFront(): void {
    for (const id of this.selection.ids) this.layer.bringToFront(id);
    this.notifyChange();
  }

  sendToBack(): void {
    for (const id of this.selection.ids) this.layer.sendToBack(id);
    this.notifyChange();
  }

  moveSelection(dx: number, dy: number): void {
    for (const el of this.selection.elements) {
      if (el.type === 'connector' || el.type === 'rope') continue;
      this.surface.update(el.id, { x: el.x + dx, y: el.y + dy });
    }
  }

  // ── 事件绑定 ──────────────────────────────────────────
  private attachEvents(): void {
    const container = this.container;
    const onPointerDown = (e: PointerEvent) => this.handlePointerDown(e);
    const onPointerMove = (e: PointerEvent) => this.handlePointerMove(e);
    const onPointerUp = (e: PointerEvent) => this.handlePointerUp(e);
    const onWheel = (e: WheelEvent) => this.handleWheel(e);
    const onDoubleClick = (e: MouseEvent) => this.handleDoubleClick(e);
    const onContextMenu = (e: MouseEvent) => e.preventDefault();
    const onKeyDown = (e: KeyboardEvent) => this.handleKeyDown(e);
    const onKeyUp = (e: KeyboardEvent) => this.handleKeyUp(e);
    const onBlur = () => {
      this.spacePressed = false;
    };

    container.addEventListener('pointerdown', onPointerDown);
    container.addEventListener('pointermove', onPointerMove);
    container.addEventListener('pointerup', onPointerUp);
    container.addEventListener('pointercancel', onPointerUp);
    container.addEventListener('wheel', onWheel, { passive: false });
    container.addEventListener('dblclick', onDoubleClick);
    container.addEventListener('contextmenu', onContextMenu);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);

    this.detach.push(() => {
      container.removeEventListener('pointerdown', onPointerDown);
      container.removeEventListener('pointermove', onPointerMove);
      container.removeEventListener('pointerup', onPointerUp);
      container.removeEventListener('pointercancel', onPointerUp);
      container.removeEventListener('wheel', onWheel);
      container.removeEventListener('dblclick', onDoubleClick);
      container.removeEventListener('contextmenu', onContextMenu);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    });
  }

  private syncRect(): void {
    const rect = this.container.getBoundingClientRect();
    this.viewport.left = rect.left;
    this.viewport.top = rect.top;
  }

  private eventToScreen(e: { clientX: number; clientY: number }): IVec {
    const rect = this.container.getBoundingClientRect();
    return [e.clientX - rect.left, e.clientY - rect.top];
  }

  private eventToModel(e: { clientX: number; clientY: number }): IVec {
    this.syncRect();
    return this.viewport.clientToModel([e.clientX, e.clientY]);
  }

  private isEditableTarget(e: Event): boolean {
    const target = e.target as HTMLElement | null;
    return Boolean(
      target &&
        (target.isContentEditable ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'INPUT' ||
          target.closest('[contenteditable="true"]'))
    );
  }

  private handlePointerDown(e: PointerEvent): void {
    if (this.tour) this.stopTour();
    if (this.isEditableTarget(e)) return;
    const screen = this.eventToScreen(e);
    const model = this.eventToModel(e);
    this.container.setPointerCapture(e.pointerId);

    // 平移：中键 / 空格 / 手型工具
    if (e.button === 1 || this.spacePressed || this.tool === 'hand') {
      this.interaction = { mode: 'panning', last: screen };
      this.container.style.cursor = 'grabbing';
      return;
    }
    if (e.button !== 0) return;

    // 只读模式：仅允许点选，不创建/拖动/缩放
    if (this.readonly) {
      const hit = this.surface.hitTest(model);
      if (hit) this.selection.set([hit.id]);
      else this.selection.clear();
      return;
    }

    if (this.tool === 'eraser') {
      const hit = this.surface.hitTest(model);
      if (hit) {
        this.pushHistory();
        this.surface.remove(hit.id);
        this.notifyChange();
      }
      return;
    }

    if (this.tool === 'note') {
      const el = this.addNote(model);
      this.selection.set([el.id]);
      this.setTool('select');
      return;
    }
    if (this.tool === 'shape') {
      const el = this.addShape(model);
      this.selection.set([el.id]);
      this.setTool('select');
      return;
    }
    if (this.tool === 'text') {
      const el = this.addText(model, '');
      this.selection.set([el.id]);
      this.setTool('select');
      this.renderer?.syncDom();
      this.domDirty = true;
      return;
    }
    if (this.tool === 'frame') {
      const el = this.addFrame(model);
      this.selection.set([el.id]);
      this.setTool('select');
      return;
    }
    if (this.tool === 'pin') {
      const el = this.addPin(model);
      this.selection.set([el.id]);
      this.setTool('select');
      return;
    }
    if (this.tool === 'tape') {
      const el = this.addTape(model);
      this.selection.set([el.id]);
      this.setTool('select');
      return;
    }
    if (this.tool === 'redact') {
      const el = this.addRedact(model);
      this.selection.set([el.id]);
      this.setTool('select');
      return;
    }
    if (this.tool === 'stamp') {
      const el = this.addStamp(model);
      this.selection.set([el.id]);
      this.setTool('select');
      return;
    }
    if (this.tool === 'brush') {
      this.interaction = { mode: 'ink', points: [model] };
      this.inkPreview = [model];
      this.scheduleRender();
      return;
    }
    if (this.tool === 'circle' || this.tool === 'highlight') {
      this.interaction = { mode: 'draw', kind: this.tool, start: model };
      this.drawPreview = { x: model[0], y: model[1], w: 0, h: 0 };
      this.scheduleRender();
      return;
    }
    if (this.tool === 'connector' || this.tool === 'rope') {
      const link = this.tool === 'rope' ? 'rope' : 'connector';
      const hit = this.surface.hitTest(model);
      if (hit && hit.type !== 'connector' && hit.type !== 'rope' && hit.type !== 'pin') {
        const anchor = nearestAnchor(hit, model);
        this.interaction = {
          mode: 'connect',
          source: { id: hit.id, anchor },
          start: anchorToPoint({ x: hit.x, y: hit.y, w: hit.w, h: hit.h }, anchor),
          targetId: hit.id,
          link,
        };
        this.connectorPreview = [this.interaction.start, model];
      } else {
        this.interaction = { mode: 'connect', source: { point: model }, start: model, link };
        this.connectorPreview = [model, model];
      }
      this.scheduleRender();
      return;
    }

    // select 工具
    // 0. 连接线 / 绳索端点拖拽
    const endHit = this.hitEndpoint(screen);
    if (endHit) {
      this.pushHistory();
      this.interaction = { mode: 'endpoint', linkId: endHit.id, end: endHit.end };
      this.scheduleRender();
      return;
    }

    // 1. 单选中时优先检查手柄
    const handleIndex = this.hitHandle(screen);
    if (handleIndex !== -1 && this.selection.size === 1) {
      const el = this.selection.elements[0];
      const bound = { x: el.x, y: el.y, w: el.w, h: el.h };
      const group: Interaction = {
        mode: 'resize',
        handle: HANDLES[handleIndex],
        startBound: bound,
        startElements: [el].map(item => ({ id: item.id, x: item.x, y: item.y, w: item.w, h: item.h })),
      };
      this.pushHistory();
      this.interaction = group;
      return;
    }

    const hit = this.surface.hitTest(model);
    if (hit) {
      const additive = e.shiftKey || e.metaKey || e.ctrlKey;
      if (!this.selection.has(hit.id)) {
        this.selection.selectAt(model, additive);
      } else if (additive) {
        this.selection.toggle(hit.id);
      }
      this.pushHistory();
      this.interaction = { mode: 'drag', start: model, last: model, moved: false };
      this.container.style.cursor = 'move';
      return;
    }

    // 空白处：框选
    this.interaction = { mode: 'marquee', start: model, additive: e.shiftKey };
    if (!e.shiftKey) this.selection.clear();
    this.activeMarquee = { x: model[0], y: model[1], w: 0, h: 0 };
    this.scheduleRender();
  }

  private handlePointerMove(e: PointerEvent): void {
    const model = this.eventToModel(e);
    const screen = this.eventToScreen(e);

    switch (this.interaction.mode) {
      case 'idle': {
        if (this.tool === 'select' && !this.spacePressed) {
          const hit = this.surface.hitTest(model);
          if (hit?.id !== this.hoverId) {
            this.hoverId = hit?.id ?? null;
            this.scheduleRender();
          }
          const handleIndex = this.hitHandle(screen);
          this.container.style.cursor =
            handleIndex !== -1 ? HANDLE_CURSORS[handleIndex] : hit ? 'default' : 'default';
        }
        return;
      }
      case 'panning': {
        this.viewport.panBy(screen[0] - this.interaction.last[0], screen[1] - this.interaction.last[1]);
        this.interaction.last = screen;
        this.scheduleRender();
        return;
      }
      case 'drag': {
        const dx = model[0] - this.interaction.last[0];
        const dy = model[1] - this.interaction.last[1];
        if (dx === 0 && dy === 0) return;
        this.interaction.moved = true;
        this.interaction.last = model;
        for (const el of this.selection.elements) {
          if (el.type === 'connector' || el.locked) continue;
          this.surface.update(el.id, { x: el.x + dx, y: el.y + dy });
        }
        this.scheduleRender();
        return;
      }
      case 'resize': {
        this.applyResize(model);
        this.scheduleRender();
        return;
      }
      case 'marquee': {
        this.activeMarquee = normalizeBound(this.interaction.start, model);
        this.selection.selectInBound(this.activeMarquee, this.interaction.additive);
        this.scheduleRender();
        return;
      }
      case 'connect': {
        this.connectorPreview = [this.interaction.start, model];
        this.scheduleRender();
        return;
      }
      case 'draw': {
        this.drawPreview = normalizeBound(this.interaction.start, model);
        this.scheduleRender();
        return;
      }
      case 'ink': {
        const pts = this.interaction.points;
        const last = pts[pts.length - 1];
        if (Math.hypot(model[0] - last[0], model[1] - last[1]) > 2 / this.viewport.zoom) {
          pts.push(model);
          this.inkPreview = pts;
          this.scheduleRender();
        }
        return;
      }
      case 'endpoint': {
        const hit = this.surface.hitTest(model);
        const snap =
          hit &&
          hit.id !== this.interaction.linkId &&
          hit.type !== 'connector' &&
          hit.type !== 'rope' &&
          hit.type !== 'pin'
            ? hit
            : null;
        this.endpointSnapId = snap?.id ?? null;
        const link = this.surface.get(this.interaction.linkId);
        if (!link) return;
        const props = link.props as Record<string, unknown>;
        const endpoint = snap
          ? { id: snap.id, anchor: nearestAnchor(snap, model) }
          : { point: model };
        this.surface.update(link.id, {
          props: { ...props, [this.interaction.end]: endpoint },
        });
        this.surface.reindexConnectorsFor(link.id);
        this.scheduleRender();
        return;
      }
      default:
        return;
    }
  }

  private handlePointerUp(e: PointerEvent): void {
    try {
      this.container.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    const model = this.eventToModel(e);
    const interaction = this.interaction;

    if (interaction.mode === 'connect') {
      const hit = this.surface.hitTest(model);
      const target: ConnectorEndpoint | null =
        hit &&
        hit.type !== 'connector' &&
        hit.type !== 'rope' &&
        hit.type !== 'pin' &&
        hit.id !== interaction.targetId
          ? { id: hit.id, anchor: nearestAnchor(hit, model) }
          : null;
      this.connectorPreview = null;
      if (target) {
        if (interaction.link === 'rope') this.addRope(interaction.source, target);
        else this.addConnector(interaction.source, target, 'orth');
      } else if (interaction.source.point) {
        if (interaction.link === 'rope') this.addRope(interaction.source, { point: model });
        else this.addConnector(interaction.source, { point: model }, 'straight');
      }
      this.notifyChange();
    }

    if (interaction.mode === 'draw' && this.drawPreview) {
      const bound = this.drawPreview;
      const el = interaction.kind === 'circle' ? this.addCircle(bound) : this.addHighlight(bound);
      this.selection.set([el.id]);
      this.drawPreview = null;
      this.setTool('select');
    }

    if (interaction.mode === 'ink') {
      const el = this.addBrush(interaction.points);
      this.inkPreview = null;
      if (el) this.selection.set([el.id]);
      this.setTool('select');
    }

    this.endpointSnapId = null;

    this.activeMarquee = null;
    this.interaction = { mode: 'idle' };
    this.container.style.cursor = this.tool === 'hand' ? 'grab' : 'default';
    this.scheduleRender();
    this.notifyChange();
  }

  /** 命中连接线 / 绳索的端点手柄。 */
  private hitEndpoint(screen: IVec): { id: string; end: 'source' | 'target' } | null {
    if (this.selection.size !== 1) return null;
    const el = this.selection.elements[0];
    if (!el || (el.type !== 'connector' && el.type !== 'rope')) return null;
    const props = el.props as unknown as ConnectorProps & { sag?: number };
    const ends: [('source' | 'target'), IVec][] = [
      ['source', resolveEndpoint(props.source, this.surface.lookup)],
      ['target', resolveEndpoint(props.target, this.surface.lookup)],
    ];
    for (const [end, point] of ends) {
      const p = this.viewport.modelToScreen(point);
      if (Math.hypot(p[0] - screen[0], p[1] - screen[1]) <= 9) return { id: el.id, end };
    }
    return null;
  }

  private hitHandle(screen: IVec): number {    if (this.selection.size !== 1) return -1;
    const el = this.selection.elements[0];
    if (!el || el.type === 'connector') return -1;
    const bound = { x: el.x, y: el.y, w: el.w, h: el.h };
    for (let i = 0; i < HANDLES.length; i++) {
      const point = anchorToPoint(bound, HANDLES[i]);
      const p = this.viewport.modelToScreen(point);
      if (Math.abs(p[0] - screen[0]) <= 7 && Math.abs(p[1] - screen[1]) <= 7) return i;
    }
    return -1;
  }

  private applyResize(model: IVec): void {
    if (this.interaction.mode !== 'resize') return;
    const { handle, startBound } = this.interaction;
    let x = startBound.x;
    let y = startBound.y;
    let w = startBound.w;
    let h = startBound.h;
    const right = startBound.x + startBound.w;
    const bottom = startBound.y + startBound.h;

    if (handle[0] === 0) {
      x = Math.min(model[0], right - 20);
      w = right - x;
    } else if (handle[0] === 1) {
      w = Math.max(20, model[0] - startBound.x);
    }
    if (handle[1] === 0) {
      y = Math.min(model[1], bottom - 20);
      h = bottom - y;
    } else if (handle[1] === 1) {
      h = Math.max(20, model[1] - startBound.y);
    }

    const el = this.selection.elements[0];
    if (!el || el.locked) return;
    this.surface.update(el.id, { x, y, w, h });
  }

  private handleWheel(e: WheelEvent): void {
    e.preventDefault();
    if (this.tour) this.stopTour();
    const screen = this.eventToScreen(e);
    if (e.ctrlKey || e.metaKey) {
      const factor = Math.exp(-e.deltaY * 0.0015);
      this.viewport.zoomTo(this.viewport.zoom * factor, screen);
    } else {
      this.viewport.panBy(-e.deltaX, -e.deltaY);
    }
    this.scheduleRender();
    this.notifyChange();
  }

  private handleDoubleClick(e: MouseEvent): void {
    if (this.isEditableTarget(e)) return;
    const model = this.eventToModel(e);
    const hit = this.surface.hitTest(model);
    if (!hit) {
      const el = this.addNote(model);
      this.selection.set([el.id]);
      this.domDirty = true;
      this.scheduleRender();
      setTimeout(() => this.renderer?.syncDom(), 0);
      return;
    }
    if (hit.type === 'note' || hit.type === 'text') {
      this.selection.set([hit.id]);
      setTimeout(() => this.renderer?.syncDom(), 0);
    }
  }

  // ── 键盘 ──────────────────────────────────────────────
  private handleKeyDown(e: KeyboardEvent): void {
    if (this.isEditableTarget(e)) {
      if (e.key === 'Escape') (e.target as HTMLElement)?.blur?.();
      return;
    }
    const mod = e.metaKey || e.ctrlKey;
    if (this.readonly) {
      // 预览模式：保留缩放/适应视图/取消选择
      if (mod && e.key === '0') {
        e.preventDefault();
        this.resetZoom();
      } else if (mod && e.key === '1') {
        e.preventDefault();
        this.fitToScreen();
      } else if (e.key === 'Escape') {
        this.selection.clear();
        this.scheduleRender();
      }
      return;
    }
    if (e.code === 'Space' && !this.spacePressed) {
      this.spacePressed = true;
      this.container.style.cursor = 'grab';
      e.preventDefault();
      return;
    }
    if (mod && e.key.toLowerCase() === 'z' && !e.shiftKey) {
      e.preventDefault();
      this.undo();
      return;
    }
    if (mod && (e.key.toLowerCase() === 'y' || (e.key.toLowerCase() === 'z' && e.shiftKey))) {
      e.preventDefault();
      this.redo();
      return;
    }
    if (mod && e.key.toLowerCase() === 'a') {
      e.preventDefault();
      this.selection.set(this.surface.all().map(el => el.id));
      return;
    }
    if (mod && e.key.toLowerCase() === 'd') {
      e.preventDefault();
      this.duplicateSelection();
      return;
    }
    if (mod && e.key.toLowerCase() === 'g') {
      e.preventDefault();
      if (e.shiftKey) this.ungroupSelection();
      else this.groupSelection();
      return;
    }
    if (mod && e.key === '0') {
      e.preventDefault();
      this.resetZoom();
      return;
    }
    if (mod && e.key === '1') {
      e.preventDefault();
      this.fitToScreen();
      return;
    }
    if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      this.deleteSelection();
      return;
    }
    if (e.key === 'Escape') {
      this.selection.clear();
      this.connectorPreview = null;
      this.inkPreview = null;
      this.drawPreview = null;
      this.interaction = { mode: 'idle' };
      this.scheduleRender();
      return;
    }
    const step = e.shiftKey ? 20 : 2;
    if (e.key === 'ArrowLeft' && this.selection.size) {
      e.preventDefault();
      this.moveSelection(-step, 0);
    }
    if (e.key === 'ArrowRight' && this.selection.size) {
      e.preventDefault();
      this.moveSelection(step, 0);
    }
    if (e.key === 'ArrowUp' && this.selection.size) {
      e.preventDefault();
      this.moveSelection(0, -step);
    }
    if (e.key === 'ArrowDown' && this.selection.size) {
      e.preventDefault();
      this.moveSelection(0, step);
    }
  }

  private handleKeyUp(e: KeyboardEvent): void {
    if (e.code === 'Space') {
      this.spacePressed = false;
      if (this.interaction.mode !== 'panning') {
        this.container.style.cursor = this.tool === 'hand' ? 'grab' : 'default';
      }
    }
  }

  // ── 工具 ──────────────────────────────────────────────
  setTool(tool: ToolName): void {
    this.tool = tool;
    this.connectorPreview = null;
    this.container.style.cursor = tool === 'hand' ? 'grab' : 'default';
    this.scheduleRender();
    this.notifyChange();
  }

  // ── 序列化 ────────────────────────────────────────────
  toJSON(): { elements: GfxElement[]; viewport: { viewportX: number; viewportY: number; zoom: number } } {
    return {
      elements: this.surface.all(),
      viewport: {
        viewportX: this.viewport.viewportX,
        viewportY: this.viewport.viewportY,
        zoom: this.viewport.zoom,
      },
    };
  }

  loadElements(elements: GfxElement[]): void {
    this.surface.replaceAll(elements);
    this.selection.clear();
    this.notifyChange();
  }

  get stats() {
    const all = this.surface.all();
    return {
      total: all.length,
      notes: all.filter(el => el.type === 'note').length,
      shapes: all.filter(el => el.type === 'shape').length,
      connectors: all.filter(el => el.type === 'connector').length,
      selected: this.selection.size,
      zoom: this.viewport.zoom,
    };
  }
}

function normalizeBound(a: IVec, b: IVec): IBound {
  return {
    x: Math.min(a[0], b[0]),
    y: Math.min(a[1], b[1]),
    w: Math.abs(a[0] - b[0]),
    h: Math.abs(a[1] - b[1]),
  };
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

/** 选择离点击点最近的锚点。 */
function nearestAnchor(el: GfxElement, point: IVec): IVec {
  const bound = { x: el.x, y: el.y, w: el.w, h: el.h };
  let best = ANCHORS[0];
  let bestDist = Infinity;
  for (const anchor of ANCHORS) {
    const p = anchorToPoint(bound, anchor);
    const d = Math.hypot(p[0] - point[0], p[1] - point[1]);
    if (d < bestDist) {
      bestDist = d;
      best = anchor;
    }
  }
  return best;
}

export { orthPath, distanceToSegment, buildConnectorPoints };